import React, { useEffect, useState } from "react";

import { ActionID, IconType } from "components/common/basic";
import LibraryPopup from "components/common/pages/ComponentLibrary/Popups/LibraryPopup";
import {
  AccountAddress,
  AccountEmail,
  AccountName,
  AccountPhone,
  AddTeam,
  BlockedManualInvoicing,
  ClientCreation,
  ConfirmAction,
  DeleteAccount,
  DraftInvoiceInfo,
  FreelanceProfile,
  OnboardingVideo,
  PaymentInfo,
  PhoneNumberVerification,
  RedirectWithMessage,
  ReferencePerson,
  StepLoaderPopup,
  TeamInfo,
  TeamMember,
  ViewBankAccount,
} from "components/common/popups";
import AddBankAccount from "components/common/popups/AddBankAccount";
import { PopupVariant } from "components/common/popups/PopupWrapper";
import {
  AccountFragment,
  BankAccountFragment,
  ClientFragment,
  ErrorFragment,
  FreelanceProfileFragment,
  HelpArticleFragment,
  InvoiceFragment,
  ReferencePersonFragment,
} from "graphql/fragments";
import { ErrorCodeEnum, FreelanceTypeEnum } from "graphql/types";
import { TTextColor } from "tw-generated";

import { useDialog } from "./useDialog";

type ErrorHandler = (
  errorMessage: string,
  payload?: unknown
) => Promise<unknown>;

interface PopupBase {
  id: string;
  variant?: PopupVariant;
  isClosing?: boolean;
  isChanging?: boolean;
}

interface AccountAddressPopup extends PopupBase {
  id: "AccountAddress";
}
interface AccountEmailPopup extends PopupBase {
  id: "AccountEmail";
}
interface AccountNamePopup extends PopupBase {
  id: "AccountName";
  props: { name: string };
}
interface AccountPhonePopup extends PopupBase {
  id: "AccountPhone";
  props: { phone: string };
}

interface RedirectWithMessagePopup extends PopupBase {
  id: "RedirectWithMessage";
  props: {
    title: string;
    subTitle?: string;
    linkProps?: {
      id: ActionID;
      to: string;
      label: string;
    };
    isClosable?: boolean;
  };
}

interface OnboardingVideoPopup extends PopupBase {
  id: "OnboardingVideo";
}

interface DraftInvoiceInfoPopup extends PopupBase {
  id: "DraftInvoiceInfo";
  props: { invoice: InvoiceFragment };
}
interface PaymentInfoPopup extends PopupBase {
  id: "PaymentInfo";
  props: { invoice: InvoiceFragment };
}

interface BlockedManualInvoicingPopup extends PopupBase {
  id: "BlockedManualInvoicing";
  props: {
    helpArticle: HelpArticleFragment;
    clientId: string;
    freelanceProfileId: string;
  };
}
interface ConfirmActionPopup extends PopupBase {
  id: "ConfirmAction";
  props: {
    onConfirmation: () => void;
    confirmationLabel: string;
    confirmationHeading: string;
    confirmationBody: string | React.JSX.Element;
    isDestructive?: boolean;
    closeOnConfirmation?: "none" | "one" | "all";
  };
}
interface ClientCreationPopup extends PopupBase {
  id: "ClientCreation";
  props: {
    clientOrFreelanceProfileId: ClientFragment | string;
    onSuccess?: (client: ClientFragment) => void;
  };
}
interface FreelanceProfilePopup extends PopupBase {
  id: "FreelanceProfile";
  props: {
    freelanceType: FreelanceTypeEnum;
    profile?: FreelanceProfileFragment;
    onSuccess?: (profile: FreelanceProfileFragment) => void;
  };
}
interface ReferencePersonPopup extends PopupBase {
  id: "ReferencePerson";
  props: {
    parent: ClientFragment | FreelanceProfileFragment;
    referencePerson: ReferencePersonFragment | null;
    onSuccess: (person: ReferencePersonFragment) => void;
  };
}
interface StepLoaderPopup extends PopupBase {
  id: "StepLoader";
  props: {
    title: string;
    steps: string[];
    action: (
      onSuccess: () => void,
      onError: (errors: ErrorFragment[]) => void
    ) => void;
    isAutoComplete?: boolean;
    onComplete?: () => void;
  };
}

interface AddBankAccountPopup extends PopupBase {
  id: "AddBankAccount";
  props?: { freelanceProfileId?: string; onSuccess?: () => void };
}

interface ViewBankAccountPopup extends PopupBase {
  id: "ViewBankAccount";
  props: { bankAccount: BankAccountFragment };
}

interface AddTeamPopup extends PopupBase {
  id: "AddTeam";
  props: { onSuccess?: (account: AccountFragment) => void };
}
interface DeleteAccountPopup extends PopupBase {
  id: "DeleteAccount";
}
interface TeamMemberPopup extends PopupBase {
  id: "TeamMember";
  props?: { account?: AccountFragment; onSuccess?: () => void };
}
interface TeamInfoPopup extends PopupBase {
  id: "TeamInfo";
  props: {
    account: AccountFragment;
    onSuccess?: (account: AccountFragment) => void;
  };
}

interface LibraryPopup extends PopupBase {
  id: "LibraryPopup";
  props: {
    title: string;
    popupSize?: "sm" | "md" | "lg";
    subTitle?: string;
    leadingIcon?: IconType;
    leadingIconColor?: TTextColor;
  };
}

export type Popup =
  | AccountAddressPopup
  | AccountEmailPopup
  | AccountNamePopup
  | AccountPhonePopup
  | RedirectWithMessagePopup
  | DraftInvoiceInfoPopup
  | OnboardingVideoPopup
  | PaymentInfoPopup
  | BlockedManualInvoicingPopup
  | ConfirmActionPopup
  | ClientCreationPopup
  | FreelanceProfilePopup
  | ReferencePersonPopup
  | AddBankAccountPopup
  | ViewBankAccountPopup
  | AddTeamPopup
  | DeleteAccountPopup
  | TeamMemberPopup
  | TeamInfoPopup
  | StepLoaderPopup
  | LibraryPopup;

interface Context {
  handleSecondSecurityLevelRequiredError: ErrorHandler;
  openPopup: (popup: Popup, replaceExisting?: boolean) => void;
  closeAllPopups: () => void;
  closeOnePopup: () => void;
  isClosing: boolean;
  isChanging: boolean;
}

export const PopupContext = React.createContext<Context>({
  handleSecondSecurityLevelRequiredError: () => new Promise(() => null),
  openPopup: () => null,
  closeAllPopups: () => null,
  closeOnePopup: () => null,
  isClosing: false,
  isChanging: false,
});

interface Props {
  children?: React.ReactNode;
}

export const PopupProvider = ({ children }: Props): React.JSX.Element => {
  const [isClosing, setIsClosing] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [activePopups, setActivePopups] = useState<Popup[]>([]);

  const shouldReduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const animationDuration = 400;

  const closeAllPopups = () => {
    const hasSlideOver = activePopups.some(
      (popup) => popup.variant === "slideOver"
    );
    const onClose = () => setActivePopups([]);
    if (!hasSlideOver || shouldReduceMotion) return onClose();

    setIsClosing(true);
    setTimeout(onClose, animationDuration);
  };

  const closeOnePopup = () => {
    const popupToClose = activePopups[activePopups.length - 1];
    const isSlideOver = popupToClose.variant === "slideOver";
    const onClose = () => setActivePopups(activePopups.slice(0, -1));
    if (!isSlideOver || shouldReduceMotion) return onClose();

    const hasPreviousPopup = activePopups.length > 1;
    if (hasPreviousPopup) {
      setIsChanging(true);
      setActivePopups([...activePopups.slice(0, -1)]);
      return setTimeout(() => setIsChanging(false), animationDuration);
    }

    setIsClosing(true);
    setTimeout(onClose, animationDuration);
  };

  const {
    reject: rejectPhoneDialog,
    resolve: resolvePhoneDialog,
    isOpen: isPhoneDialogOpened,
    handleError: handleSecondSecurityLevelRequiredError,
  } = useDialog((code: string) => code === ErrorCodeEnum.AuthLevel_2Required);

  // Close popups on browser navigation
  useEffect(() => {
    window.addEventListener("popstate", closeAllPopups);

    return () => window.removeEventListener("popstate", closeAllPopups);
  }, []);

  // Set is closing after popup has been closed
  useEffect(() => {
    setIsClosing(false);
  }, [activePopups.length]);

  return (
    <PopupContext.Provider
      value={{
        handleSecondSecurityLevelRequiredError,
        openPopup: (popup, replaceExisting) => {
          const variant = ((popupId) => {
            switch (popupId) {
              case "TeamMember":
                return "slideOver";

              default:
                return "popup";
            }
          })(popup.id);

          if (
            (popup.variant ?? variant) === "slideOver" &&
            activePopups[0]?.variant === "slideOver"
          ) {
            setIsChanging(true);
            setTimeout(() => setIsChanging(false), animationDuration);
          }

          setActivePopups(
            replaceExisting
              ? [...activePopups.slice(0, -1), { variant, ...popup }]
              : [...activePopups, { variant, ...popup }]
          );
        },
        closeAllPopups,
        closeOnePopup,
        isClosing,
        isChanging,
      }}
    >
      {children}

      <div>
        {activePopups.map((popup, index) => {
          const key = `${popup.id}:${index}`;
          const props = {
            variant: popup.variant ?? "popup",
            hasPrevious: index > 0,
          };

          switch (popup?.id) {
            case "AccountAddress":
              return <AccountAddress key={key} {...props} />;
            case "AccountEmail":
              return <AccountEmail key={key} {...props} />;
            case "AccountName":
              return <AccountName key={key} {...props} {...popup.props} />;
            case "AccountPhone":
              return <AccountPhone key={key} {...props} {...popup.props} />;

            case "RedirectWithMessage":
              return <RedirectWithMessage key={key} {...props} {...popup.props} />; // prettier-ignore

            case "OnboardingVideo":
              return <OnboardingVideo key={key} {...props} />;

            case "PaymentInfo":
              return <PaymentInfo key={key} {...props} {...popup.props} />;

            case "DraftInvoiceInfo":
              return <DraftInvoiceInfo key={key} {...props} {...popup.props} />;

            case "BlockedManualInvoicing":
              return <BlockedManualInvoicing key={key} {...props} {...popup.props} />; // prettier-ignore

            case "ConfirmAction":
              return <ConfirmAction key={key} {...props} {...popup.props} />;

            case "ClientCreation":
              return <ClientCreation key={key} {...props} {...popup.props} />;

            case "FreelanceProfile":
              return <FreelanceProfile key={key} {...props} {...popup.props} />;

            case "ReferencePerson":
              return <ReferencePerson key={key} {...props} {...popup.props} />;

            case "AddBankAccount":
              return <AddBankAccount key={key} {...props} {...popup.props} />;

            case "ViewBankAccount":
              return <ViewBankAccount key={key} {...props} {...popup.props} />;

            case "StepLoader":
              return <StepLoaderPopup key={key} {...props} {...popup.props} />;

            case "AddTeam":
              return <AddTeam key={key} {...props} {...popup.props} />;

            case "DeleteAccount":
              return <DeleteAccount key={key} {...props} />;

            case "TeamMember":
              return <TeamMember key={key} {...props} {...popup.props} />;

            case "TeamInfo":
              return <TeamInfo key={key} {...props} {...popup.props} />;

            case "LibraryPopup":
              return <LibraryPopup key={key} {...props} {...popup.props} />;

            default:
              return null;
          }
        })}
      </div>

      {isPhoneDialogOpened && (
        <PhoneNumberVerification
          onClose={rejectPhoneDialog}
          onSuccessfulVerification={resolvePhoneDialog}
        />
      )}
    </PopupContext.Provider>
  );
};
